import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBolt,
  faBoxesStacked,
  faBuilding,
  faCheck,
  faChevronDown,
  faFlaskVial,
  faGear,
  faHelmetSafety,
  faTag,
  faTriangleExclamation,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { GloveBrandAlternatives } from '../components/GloveBrandAlternatives'
import { whatsAppCtaProps, trackSelectItem } from '../lib/analytics'
import { getStorefrontConfig, listStorefrontCategories } from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig } from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBrandLandingSchema, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import { listDashboardNewsPosts } from '../lib/news'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/sarung-tangan-gekko'
const PAGE_CATEGORY = 'Sarung Tangan Gekko Grip'
const HERO_IMAGE = '/lp/sarung-tangan-gekko-spread.jpeg'

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

export const GEKKO_PRODUCTS = [
  {
    name: 'Sarung Tangan Gekko Grip Nitrile Biru',
    slug: 'sarung-tangan-gekko-grip-nitrile-biru',
    image: '/lp/gekko-biru.jpeg',
    coating: 'Nitrile biru',
    liner: 'Rajut putih',
    fit: 'Perawatan mesin, penanganan komponen, bengkel, dan area kerja yang berpotensi berminyak.',
    note: 'Warna terang memudahkan inspeksi visual kondisi sarung tangan.',
  },
  {
    name: 'Sarung Tangan Gekko Grip Nitrile Hitam',
    slug: 'sarung-tangan-gekko-grip-nitrile-hitam',
    image: '/lp/gekko-hitam.jpeg',
    coating: 'Nitrile hitam',
    liner: 'Polyester hitam',
    fit: 'Bengkel, otomotif, mekanikal, dan pekerjaan kotor yang membutuhkan grip telapak.',
    note: 'Varian ini memakai warna hitam. Periksa ukuran dan kode serinya sebelum memesan.',
  },
  {
    name: 'Sarung Tangan Gekko Grip Nitrile Putih Hitam',
    slug: 'sarung-tangan-gekko-grip-nitrile-putih-hitam',
    image: '/lp/gekko-putih.jpeg',
    coating: 'Nitrile hitam',
    liner: 'Rajut putih',
    fit: 'Produksi, perawatan mesin, gudang, dan pengadaan yang mengutamakan inspeksi visual.',
    note: 'Kontras bahan rajut dan lapisan membantu pengecekan kondisi sebelum digunakan.',
  },
  {
    name: 'Sarung Tangan Gekko PU Biru',
    slug: 'sarung-tangan-gekko-pu-biru',
    image: '/lp/gekko-biru.jpeg',
    coating: 'PU biru',
    liner: 'Rajut putih',
    fit: 'Perakitan, pengepakan, gudang, dan penanganan barang dengan permukaan kering.',
    note: 'Lapisan PU yang tipis membantu mempertahankan ketangkasan tangan saat menangani komponen.',
  },
  {
    name: 'Sarung Tangan Gekko PU Hitam',
    slug: 'sarung-tangan-gekko-pu-hitam',
    image: '/lp/gekko-hitam.jpeg',
    coating: 'PU hitam',
    liner: 'Polyester hitam',
    fit: 'Perakitan, pengepakan, gudang, dan penanganan barang dengan permukaan kering.',
    note: 'Varian ini memakai warna hitam. Periksa ukuran dan kode serinya sebelum memesan.',
  },
  {
    name: 'Sarung Tangan Gekko PU Putih-Hitam',
    slug: 'sarung-tangan-gekko-pu-putih-hitam',
    image: '/lp/gekko-putih.jpeg',
    coating: 'PU hitam',
    liner: 'Rajut putih',
    fit: 'Perakitan, pengepakan, gudang, dan penanganan barang dengan permukaan kering.',
    note: 'Warna rajut yang terang memudahkan pemeriksaan kondisi sarung tangan sebelum dipakai.',
  },
] as const

const USE_CASES = [
  {
    icon: faGear,
    title: 'Bengkel & otomotif',
    desc: 'Untuk memegang alat dan komponen saat pekerjaan mekanikal. Seri nitrile lebih relevan ketika permukaan benda terkena minyak ringan.',
  },
  {
    icon: faBuilding,
    title: 'Pabrik & perawatan mesin',
    desc: 'Untuk perawatan mesin, penanganan komponen, dan aktivitas produksi umum yang membutuhkan grip pada telapak.',
  },
  {
    icon: faHelmetSafety,
    title: 'Proyek & konstruksi',
    desc: 'Untuk pekerjaan lapangan dan penanganan material. Pilih Nitrile atau PU setelah mempertimbangkan permukaan benda dan risiko kerja.',
  },
  {
    icon: faBoxesStacked,
    title: 'Gudang & logistik',
    desc: 'Membantu grip saat memindahkan karton, komponen, dan barang. Pilih seri berdasarkan permukaan barang dan kondisi area kerja.',
  },
]

const USE_LIMITATIONS = [
  'Kontak dengan bahan kimia tertentu tanpa data permeasi atau standar yang sesuai.',
  'Risiko sayatan tinggi tanpa level cut resistance yang terverifikasi.',
  'Pekerjaan listrik, panas ekstrem, atau welding tanpa spesifikasi khusus.',
  'Kebutuhan medis, food-grade, atau sarung tangan disposable sekali pakai.',
]

const DROPIFY_REASONS = [
  {
    icon: faBolt,
    title: 'Sesuaikan seri dengan pekerjaan',
    desc: 'Kirim kondisi kerja, seri yang diminati, dan kota tujuan. Admin membantu memilih Nitrile atau PU serta mengecek stoknya.',
  },
  {
    icon: faBoxesStacked,
    title: 'Sample sebelum order besar',
    desc: 'Sample dihitung harga satuan ditambah ongkir. Gunakan untuk mengecek ukuran, grip, dan kenyamanan sebelum membeli dalam jumlah grosir.',
  },
  {
    icon: faTag,
    title: 'Harga volume per seri',
    desc: 'Harga grosir dihitung berdasarkan seri, ukuran, dan jumlah pembelian. Admin menyiapkan penawaran sesuai kebutuhan Anda.',
  },
  {
    icon: faTruckFast,
    title: 'Kirim langsung ke pabrik atau proyek',
    desc: 'Pesanan dapat dikirim ke gudang, pabrik, atau lokasi proyek di seluruh Indonesia dengan ekspedisi yang disesuaikan.',
  },
]

const GEKKO_TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Seri Gekko mana yang cocok untuk bengkel dengan permukaan berminyak?',
    a: 'Seri nitrile biasa dipilih untuk bengkel, maintenance mesin, dan handling komponen yang permukaannya berminyak. Ceritakan kondisi kerja tim Anda, admin membantu memilih serinya.',
  },
  {
    q: 'Bisakah meminta sample Gekko Grip sebelum order besar?',
    a: 'Bisa. Sample dihitung dari harga satuan ditambah ongkir ke kota Anda. Pakai untuk menguji ukuran, grip, dan kenyamanan sebelum order lusinan.',
  },
  {
    q: 'Apa bedanya lapisan Nitrile dan PU pada Gekko Grip?',
    a: 'Lapisan Nitrile biasa dipilih saat benda kerja berpotensi terkena minyak ringan. Lapisan PU lebih sesuai untuk penanganan barang kering dan pekerjaan yang membutuhkan ketangkasan tangan. Pilihan akhir tetap mengikuti risiko kerja, standar K3, dan dokumen seri yang tersedia.',
  },
  {
    q: 'Pesanan pertama saya besar. Bisakah melihat barangnya dulu?',
    a: 'Minta foto stok aktual per seri sebelum membayar. Jika perlu memegang barangnya, pesan sample lebih dulu, baru lanjut ke jumlah grosir.',
  },
  {
    q: 'Berapa lama Gekko Grip sampai di kota saya?',
    a: 'Barang dikirim dari Surabaya setelah pembayaran dikonfirmasi dan pesanan selesai disiapkan. Kirim kota tujuan kepada admin untuk meminta estimasi waktu penyiapan serta perjalanan dari ekspedisi yang dipilih.',
  },
]

export const GEKKO_FAQ: FaqItem[] = [
  {
    q: 'Apakah Gekko Grip sama dengan sarung tangan nitrile sekali pakai?',
    a: 'Beda jenis. Gekko Grip adalah sarung tangan kerja rajut dengan telapak berlapis nitrile, dipakai berulang dan dijual per lusin (12 pasang). Sarung tangan nitrile tipis yang dijual per box adalah produk sekali pakai untuk kebutuhan berbeda. Jika yang Anda cari tipe per box, sampaikan lewat WhatsApp agar admin memeriksa produk yang tersedia.',
  },
  {
    q: 'Apakah minimum order 50 lusin boleh campur seri atau warna?',
    a: 'Bisa selama kombinasi seri dan warnanya tersedia. Kirim pembagian yang Anda perlukan, misalnya 30 lusin hitam dan 20 lusin biru. Admin akan mengecek stok setiap pilihan sebelum menyiapkan quotation.',
  },
  {
    q: 'Ukuran Gekko Grip apa saja yang tersedia?',
    a: 'Ukuran yang tersedia berbeda menurut seri dan stok. Sebutkan ukuran yang biasa dipakai tim Anda. Jika masih ragu, minta sample untuk mengecek kenyamanan dan ketangkasan tangan sebelum membeli dalam jumlah besar.',
  },
  {
    q: 'Kenapa harga Gekko Grip tidak dicantumkan di halaman?',
    a: 'Harga berbeda menurut seri, jumlah pembelian, dan stok saat quotation dibuat. Kirim nama seri, jumlah per ukuran, dan kota tujuan melalui WhatsApp agar admin dapat memberikan harga volume serta ongkir yang sesuai.',
  },
  {
    q: 'Apakah Dropify melayani suplai Gekko Grip secara rutin?',
    a: 'Bisa. Kirim perkiraan pemakaian bulanan, pembagian seri dan ukuran, serta jadwal kebutuhan. Admin akan memeriksa kemampuan stok dan membahas pola pengiriman bersama tim pengadaan Anda.',
  },
  {
    q: 'Apakah pesanan bisa dikirim langsung ke pabrik atau lokasi proyek?',
    a: 'Bisa. Pesanan dapat dikirim langsung ke gudang, pabrik, atau lokasi proyek di seluruh Indonesia. Berikan kota, kecamatan, dan perkiraan jumlah agar admin dapat menghitung pilihan ekspedisi serta ongkir.',
  },
  {
    q: 'Apakah marking dan dokumen CE atau EN388 tersedia?',
    a: 'Ketersediaan marking dan dokumen berbeda menurut seri. Kirim standar yang diwajibkan oleh tim K3 atau dokumen tender. Admin akan memeriksa barang serta dokumennya sebelum Anda mencantumkan seri tersebut dalam PO.',
  },
]

const GEKKO_SCHEMA_PRODUCTS = GEKKO_PRODUCTS.map((product, index) => ({
  '@type': 'ListItem',
  position: index + 1,
  item: {
    '@type': 'Thing',
    name: product.name,
    description: `${product.coating}, ${product.liner}. ${product.fit}`,
    ...(product.image ? { image: absoluteUrl(product.image) } : {}),
    url: absoluteUrl(`/produk/${product.slug}`),
    brand: { '@type': 'Brand', name: 'Gekko Grip' },
  },
}))

export const Route = createFileRoute('/sarung-tangan-gekko')({
  loader: async () => {
    const [config, categories, proofImages, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      getBrandProofImages(),
      listDashboardNewsPosts().catch(() => []),
    ])

    return { config, categories, proofImages, newsPosts }
  },
  head: () => {
    const title = brandTitle('Grosir Sarung Tangan Nitrile & PU Gekko Grip')
    const description =
      'Grosir sarung tangan nitrile dan PU Gekko Grip untuk bengkel, pabrik, proyek, dan gudang. Rajut palm coated dipakai berulang. MOQ 50 lusin. Cek stok via WhatsApp.'

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        { property: 'og:image', content: absoluteUrl(HERO_IMAGE) },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: absoluteUrl(HERO_IMAGE) },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBrandLandingSchema({ brandName: 'Gekko Grip', pageName: title, description, path: PAGE_PATH })),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBreadcrumbSchema([
            { name: 'Beranda', path: '/' },
            { name: 'Merek', path: '/merek' },
            { name: 'Sarung Tangan Gekko Grip', path: PAGE_PATH },
          ])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema([...GEKKO_FAQ, ...GEKKO_TRUST_QUESTIONS])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Produk Sarung Tangan Gekko Grip di Dropify',
            itemListElement: GEKKO_SCHEMA_PRODUCTS,
          }),
        },
      ],
    }
  },
  component: GekkoRoute,
})

function GekkoRoute() {
  const { config, categories, proofImages, newsPosts } = Route.useLoaderData()
  return <GekkoLandingPage config={config} categories={categories} proofImages={proofImages} newsPosts={newsPosts} />
}

function WhatsAppButton({
  message,
  label,
  placement,
  itemName,
  className,
}: {
  message: string
  label: string
  placement: string
  itemName?: string
  className: string
}) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement,
          lp_category: PAGE_CATEGORY,
          ...(itemName
            ? {
                lead_item_name: itemName,
                items: [{ item_id: itemName, item_name: itemName, item_category: PAGE_CATEGORY }],
              }
            : {}),
        })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

function SectionEyebrow({ children }: { children: string }) {
  return <p className="text-xs font-black uppercase tracking-[0.2em] text-lime-700">{children}</p>
}

export function GekkoLandingPage({
  config = null,
  categories = [],
  proofImages = [],
  newsPosts = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  proofImages?: string[]
  newsPosts?: import('../lib/dropify').StorefrontBlogPost[]
}) {
  const heroMessage =
    'Halo Dropify, saya mau cek sarung tangan Gekko Grip seri ___. Kebutuhan: ___ lusin. Kota tujuan: ___. Mohon info stok dan quotation.'
  const orderMessage =
    'Halo Dropify, saya mau cek Gekko Grip seri ___. Kebutuhan: ___ lusin. Kota tujuan: ___. Mohon info stok dan harga grosirnya.'

  return (
    <div className="min-h-screen bg-[#f7f8f3] text-slate-900">
      <div className="bg-lime-300 px-4 py-2 text-center text-xs font-bold text-[#111a13] sm:text-sm">
        Minimum order mulai 50 lusin (600 pasang) · Sample dan quotation tersedia · Kirim se-Indonesia
      </div>

      <header className="border-b border-white/10 bg-[#101913] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify">
            <LogoLockup size="sm" invert />
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Periksa stok Gekko"
            placement="header"
            className="inline-flex items-center gap-2 rounded-full border border-lime-300/50 px-4 py-2.5 text-sm font-bold text-lime-200 transition-colors hover:bg-lime-300 hover:text-[#101913]"
          />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#101913] text-white">
          <div aria-hidden className="absolute -right-28 -top-28 h-96 w-96 rounded-full bg-lime-300/10 blur-3xl" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 md:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-lime-300 px-3 py-1 text-xs font-black uppercase tracking-wider text-[#101913]">Gekko Grip</span>
                <span className="rounded-full border border-white/20 px-3 py-1 text-xs font-bold text-slate-300">Telapak lapis Nitrile & PU</span>
              </div>
              <h1 className="mt-6 max-w-3xl text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                Grosir sarung tangan Gekko Grip <span className="text-lime-300">Nitrile dan PU</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
                Sarung tangan kerja rajut berlapis untuk bengkel, pabrik, proyek, dan gudang. Minimum order 50 lusin. Kirim seri, jumlah, dan kota tujuan agar admin dapat mengecek stok dan menyiapkan quotation.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <WhatsAppButton
                  message={heroMessage}
                  label="Cek stok dan minta quotation"
                  placement="hero_primary"
                  className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800"
                />
                <a href="#produk-gekko" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-bold text-white transition-colors hover:bg-white/10">
                  Lihat seri Gekko
                  <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
                </a>
              </div>
              <p className="mt-3 text-sm text-slate-400">Tidak tahu serinya? Kirim foto pekerjaan atau material yang dipegang.</p>
            </div>

            <div data-hero-product-visual className="relative mx-auto w-full max-w-xl">
              <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white p-3 shadow-2xl shadow-black/30">
                <img
                  src={HERO_IMAGE}
                  alt="Pilihan sarung tangan Gekko Grip nitrile dari Dropify"
                  width="1024"
                  height="1024"
                  fetchPriority="high"
                  decoding="async"
                  className="aspect-square w-full rounded-[1.4rem] object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -left-2 max-w-[250px] rounded-2xl bg-lime-300 p-4 text-[#101913] shadow-xl sm:left-5">
                <p className="text-xs font-black uppercase tracking-wider">Untuk pemakaian berulang</p>
                <p className="mt-1 text-sm font-medium leading-snug">Gekko adalah sarung tangan kerja rajut berlapis yang dapat digunakan kembali.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div>
              <SectionEyebrow>Kenali produknya</SectionEyebrow>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Apa itu sarung tangan Gekko Grip?</h2>
            </div>
            <div className="space-y-5 text-base leading-relaxed text-slate-600 md:text-lg">
              <p>
                Gekko Grip adalah <strong className="text-slate-900">sarung tangan kerja yang dapat digunakan berulang</strong>. Bagian dasarnya berupa rajutan, sedangkan telapak dan jarinya dilapisi nitrile atau PU untuk membantu cengkeraman. Sarung tangan dengan lapisan pada telapak ini juga dikenal sebagai palm coated atau palm fit.
              </p>
              <p>
                Sarung tangan nitrile tipis sekali pakai yang dijual per box merupakan jenis produk yang berbeda. Gekko ditujukan untuk pekerjaan mekanikal, pemindahan material, perawatan mesin, dan aktivitas operasional. Pemilihannya tetap harus mengikuti risiko kerja.
              </p>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-relaxed text-amber-950">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                  <p><strong>Catatan K3:</strong> jangan menyimpulkan semua Gekko tahan bahan kimia, sayatan, atau memiliki sertifikasi yang sama. Sebutkan standar wajib Anda agar admin dapat memeriksa seri dan dokumennya sebelum PO.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-[#f7f8f3] py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <SectionEyebrow>Pilih lapisan</SectionEyebrow>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Pilih Gekko Nitrile atau PU sesuai kondisi kerja</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Pertimbangkan permukaan benda, paparan di area kerja, durasi pemakaian, dan standar K3 yang diwajibkan.</p>
            </div>
            <div className="mt-9 grid gap-5 lg:grid-cols-2">
              <article className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-lime-300">
                  <FontAwesomeIcon icon={faGear} className="h-5 w-5" />
                </div>
                <p className="mt-6 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Gekko Grip Nitrile</p>
                <h3 className="mt-2 text-2xl font-black tracking-tight">Untuk kerja mekanikal & potensi minyak ringan</h3>
                <ul className="mt-5 space-y-3 text-sm leading-relaxed text-slate-600">
                  {[
                    'Relevan untuk bengkel, otomotif, perawatan mesin, dan penanganan komponen.',
                    'Lapisan nitrile membantu grip pada benda yang permukaannya berpotensi berminyak.',
                    'Tersedia dalam varian biru, hitam, dan putih-hitam.',
                  ].map((item) => (
                    <li key={item} className="flex gap-3"><FontAwesomeIcon icon={faCheck} className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-lime-700" />{item}</li>
                  ))}
                </ul>
              </article>
              <article className="rounded-3xl bg-[#1a2920] p-6 text-white md:p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-lime-300 text-[#101913]">
                  <FontAwesomeIcon icon={faBoxesStacked} className="h-5 w-5" />
                </div>
                <p className="mt-6 text-xs font-black uppercase tracking-[0.18em] text-lime-300">Gekko PU</p>
                <h3 className="mt-2 text-2xl font-black tracking-tight">Untuk handling barang kering dan pekerjaan presisi</h3>
                <ul className="mt-5 space-y-3 text-sm leading-relaxed text-slate-300">
                  {[
                    'Biasa dipilih untuk perakitan, pengepakan, gudang, dan penanganan komponen kering.',
                    'Lapisan PU yang tipis membantu mempertahankan ketangkasan tangan.',
                    'Tersedia dalam varian biru, hitam, dan putih-hitam.',
                  ].map((item) => (
                    <li key={item} className="flex gap-3"><FontAwesomeIcon icon={faCheck} className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-lime-300" />{item}</li>
                  ))}
                </ul>
              </article>
            </div>
            <p className="mt-4 text-sm text-slate-500">Gunakan panduan ini sebagai titik awal. Keputusan akhir tetap mengikuti risk assessment dan SOP keselamatan perusahaan.</p>
          </div>
        </section>

        <section id="produk-gekko" className="scroll-mt-6 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
              <div className="max-w-3xl">
                <SectionEyebrow>Produk yang kami jual</SectionEyebrow>
                <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Pilihan Gekko Grip di Dropify</h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">Kirim nama produk, ukuran, dan jumlah agar admin dapat memeriksa stok serta menyiapkan quotation.</p>
              </div>
              <p className="text-sm font-semibold text-slate-500">1 lusin = 12 pasang</p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {GEKKO_PRODUCTS.map((product) => (
                <article key={product.name} className="group flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:border-lime-400 hover:shadow-xl">
                  <a
                    href={`/produk/${product.slug}`}
                    aria-label={`Lihat detail ${product.name}`}
                    onClick={() => trackSelectItem({ item_id: product.slug, item_name: product.name, item_category: PAGE_CATEGORY }, 'Gekko Grip brand landing')}
                    className="relative block overflow-hidden bg-[#f5f6f0]"
                  >
                    <img
                      src={product.image}
                      alt={`Sarung tangan kerja ${product.name}`}
                      width="420"
                      height="420"
                      loading="lazy"
                      decoding="async"
                      className="aspect-square w-full object-contain p-5 transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-[#101913] px-3 py-1 text-[11px] font-black uppercase tracking-wide text-lime-300">{product.coating}</span>
                  </a>
                  <div className="flex flex-1 flex-col p-5">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Bahan dasar: {product.liner}</p>
                    <h3 className="mt-2 text-lg font-black tracking-tight text-slate-900">
                      <a
                        href={`/produk/${product.slug}`}
                        onClick={() => trackSelectItem({ item_id: product.slug, item_name: product.name, item_category: PAGE_CATEGORY }, 'Gekko Grip brand landing')}
                        className="transition-colors hover:text-lime-800"
                      >
                        {product.name}
                      </a>
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{product.fit}</p>
                    <p className="mt-3 rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">{product.note}</p>
                    <WhatsAppButton
                      message={`Halo Dropify, saya mau cek ${product.name}. Pemakaian untuk: ___. Kebutuhan: ___ lusin. Bisa kirim foto stok aktual dan harganya?`}
                      label="Cek stok seri ini"
                      placement="product_card"
                      itemName={product.name}
                      className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-3 text-sm font-black text-white transition-colors hover:bg-green-800"
                    />
                  </div>
                </article>
              ))}
            </div>
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm leading-relaxed text-slate-600">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
              <p><strong className="text-slate-900">Stok bersifat dinamis.</strong> Pilihan warna, ukuran, kemasan, cetakan standar, dan seri perlu dikonfirmasi melalui foto barang yang dikirim admin sebelum order.</p>
            </div>
          </div>
        </section>

        <BrandEasyOrder newsPosts={newsPosts} />

        <section id="kecocokan-gekko" className="scroll-mt-6 border-y border-slate-200 bg-[#f7f8f3] py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-6 px-4 lg:grid-cols-2 lg:gap-8">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
              <SectionEyebrow>Kegunaan</SectionEyebrow>
              <h2 className="mt-3 text-3xl font-black tracking-tight">Cocok untuk pekerjaan apa?</h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">Nama industri saja belum cukup. Informasikan benda yang dipegang dan kondisi areanya agar rekomendasi lebih tepat.</p>
              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {USE_CASES.map((item) => (
                  <article key={item.title} className="rounded-2xl border border-slate-200 bg-[#f7f8f3] p-4">
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-lime-700" />
                    <h3 className="mt-4 text-base font-black tracking-tight">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                  </article>
                ))}
              </div>
            </div>

            <aside className="flex flex-col rounded-3xl border border-amber-200 bg-amber-50 p-6 md:p-8">
              <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-amber-700">
                <FontAwesomeIcon icon={faFlaskVial} className="h-4 w-4" />
                Batas penggunaan
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-tight text-amber-950">Kapan Gekko belum tentu cocok?</h2>
              <p className="mt-4 text-sm leading-relaxed text-amber-900 md:text-base">Kondisi berikut membutuhkan seri dengan spesifikasi khusus atau verifikasi dokumen tambahan.</p>
              <div className="mt-7 grid flex-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 lg:grid-rows-4">
                {USE_LIMITATIONS.map((item) => (
                  <div key={item} className="flex h-full items-center gap-3 rounded-2xl border border-amber-200 bg-white/80 p-4 text-sm leading-relaxed text-amber-950">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    {item}
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </section>

        <section id="kenapa-dropify" className="scroll-mt-6 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.82fr_1.18fr] lg:gap-16">
            <div>
              <SectionEyebrow>Data pemesanan</SectionEyebrow>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Seri, stok, quotation, dan pengiriman diperiksa oleh satu admin</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Kirim kondisi kerja, seri, ukuran, jumlah, dan kota tujuan. Admin akan memeriksa stok, menyiapkan quotation, serta menginformasikan pilihan pengiriman.</p>
              <WhatsAppButton
                message={orderMessage}
                label="Chat admin Gekko"
                placement="why_dropify"
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {DROPIFY_REASONS.map((item) => (
                <article key={item.title} className="rounded-2xl border border-slate-200 bg-[#f7f8f3] p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#101913] text-lime-300">
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-black tracking-tight">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center">
              <SectionEyebrow>FAQ Gekko Grip</SectionEyebrow>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Sebelum minta penawaran</h2>
            </div>
            <div className="mt-9 divide-y divide-slate-200 border-y border-slate-200">
              {GEKKO_FAQ.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black text-slate-900">
                    {item.q}
                    <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-lime-700 transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
                  {item.link && <a href={item.link.href} className="mt-2 inline-block text-sm font-bold text-lime-800 hover:underline">{item.link.label}</a>}
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-lime-300 py-14 text-[#101913]">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.2em]">Siap cek kebutuhan Anda</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kirim kondisi kerja agar admin dapat memeriksa seri Gekko yang sesuai.</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#263429] md:text-base">Sertakan jumlah, kota tujuan, dan standar wajib agar quotation pertama lebih lengkap.</p>
            </div>
            <WhatsAppButton
              message={heroMessage}
              label="Minta quotation Gekko"
              placement="final_cta"
              className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#101913] px-7 py-4 text-base font-black text-white transition-colors hover:bg-slate-800"
            />
          </div>
        </section>

        <BrandTrustSections brandName="Gekko Grip" itemName="sarung tangan Gekko Grip" images={proofImages} questions={GEKKO_TRUST_QUESTIONS} />
        <GloveBrandAlternatives currentPath={PAGE_PATH} />
      </main>

      <Footer config={config} categories={categories} />
    </div>
  )
}
